import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { JsonLd } from 'react-schemaorg';
import map from 'lodash/map';
import get from 'lodash/get';
import Bem from 'bemHelper';
import { I18nHoc } from 'helpers/i18n';
import formatDate from 'helpers/formatDate';
import absoluteUrl from 'helpers/absoluteUrl';
import urlShortener from 'helpers/urlShortener';
import { numDescOnlyText } from 'helpers/numDesc';
import beautifyNumber from 'helpers/beautifyNumber';
import Link from 'components/link';
import ToolTip from 'components/tooltip';
import Row, { Col } from 'components/row';
import Image, { Thumb } from 'components/image';
import Icon, { iconTypes } from 'components/icon';

import { getHousesCounter } from 'src-sites/bild/section-project/render-common';
import linksDomik from 'src-sites/domik/links';

import translates from '../developer-i18n';

import 'styles/forum/section-developer/_page-developer.scss';

const bemClasses = new Bem('developer');

@I18nHoc(translates())
export default class BlockDev extends Component {
  static propTypes = {
    className: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({})),
    devImage: PropTypes.string
  };

  renderWebsite=(website, titleDeveloper)=> {
    if (!website) return null;

    return (
      <div {...bemClasses('links')}>
        <Link
          {...bemClasses('link')}
          href={website}
          title={this.i18n('developerLinkToSiteTitle')}
          dataProps={titleDeveloper ? { 'data-gtm-dev': titleDeveloper } : null}
          external
          blank
        >
          <span {...bemClasses('linkContent')}>
            <span {...bemClasses('text')} children={urlShortener(website)} />
          </span>
        </Link>
      </div>
    );
  };

  renderImageAndAvatar(main_photo, avatar, topic) {
    const { i18n } = this;
    const imgMainDeveloper = {
      id: main_photo,
      alt: i18n('developerImgAlt', { placeholder: topic }),
      title: i18n('developerImgAlt', { placeholder: topic })
    };
    const avatarDeveloper = { id: get(avatar, 'id'), alt: topic, title: topic };

    return (
      <div {...bemClasses('imgMainContainer')}>
        <Image
          {...bemClasses('imgMain')}
          imgOrigin
          image={imgMainDeveloper}
          fullScreenSwitch="none"
        />
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'ImageObject',
            name: topic ? i18n('developerImgAlt', { placeholder: topic }) : null,
            description: topic ? i18n('developerImgAlt', { placeholder: topic }) : null,
            contentUrl: main_photo ? absoluteUrl(`/images/thumb/full/${main_photo}`) : null
          }}
        />
        {avatar &&
          <Row {...bemClasses('row', 'logo')}>
            <Col allPr={0}>
              <Thumb
                {...bemClasses('logo')}
                image={avatarDeveloper}
              />
              <JsonLd
                item={{
                  '@context': 'https://schema.org',
                  '@type': 'ImageObject',
                  name: get(avatarDeveloper, 'alt', null),
                  description: get(avatarDeveloper, 'title', null),
                  contentUrl: get(avatar, 'id') ? absoluteUrl(`/images/thumb/full/${avatar.id}`) : null
                }}
              />
            </Col>
          </Row>
        }
      </div>
    );
  }

  renderPhones(phones) {
    if (!phones || !phones.length) return null;

    return (
      <div {...bemClasses('links', 'phones')}>
        {map(phones, (phone, key) => (
          <Fragment key={key}>
            <Link
              {...bemClasses('link')}
              href={`tel:${phone}`}
              title={this.i18n('developerPhoneTitle')}
              rel="nofollow"
              external
            >
              <span {...bemClasses('linkContent')}>
                <span {...bemClasses('text')} children={phone} />
              </span>
            </Link>
            {phones.length !== key + 1 &&
            ', '
            }
          </Fragment>
        ))}
      </div>
    );
  }

  render() {
    const { i18n } = this;
    const { className, devImage } = this.props;
    const data = get(this, 'props.data[0]') || {};
    const {
      avatar, work_start_date,
      living_complex_counters, company_name, phones, website, url, rel_sys
    } = data;
    const housesCounter = getHousesCounter(living_complex_counters);
    const minBuiltYear = housesCounter.ready_house_from_year ?
      i18n('developerYear', { placeholder: housesCounter.ready_house_from_year }) : '';
    const dev_index_by_lc = get(rel_sys, 'dev_index_by_lc') || {};

    return (
      <div {...bemClasses({ extra: className })}>
        {this.renderImageAndAvatar(devImage, avatar, company_name)}
        <Row {...bemClasses('row', 'main')}>
          <Col {...bemClasses('col', 'main')} allPr={0}>
            <div {...bemClasses('title')}>
              {!!(url && company_name) &&
                <Link
                  {...bemClasses('link', 'title')}
                  builder={linksDomik.sectionLanding}
                  builderProps={{
                    url,
                    title: company_name
                  }}
                  title={company_name}
                  noRedirector
                >
                  <div {...bemClasses('linkContent')}>
                    <div {...bemClasses('text')}>
                      <h2 children={company_name} />
                    </div>
                  </div>
                </Link>
              }
            </div>
            {this.renderWebsite(website, company_name)}
            {this.renderPhones(phones)}
          </Col>
          <Col {...bemClasses('col', 'data')} allPr={0} allAuto mpFlex>
            {work_start_date &&
              <>
                <div>
                  <span {...bemClasses('text', 'main')} children={formatDate({ date: work_start_date, format: 'yyyy' })} />
                </div>
                <div>
                  <span {...bemClasses('text', 'info')} children={i18n('developerFoundedYearText')} />
                </div>
              </>
            }
          </Col>
          <Col {...bemClasses('col', 'data')} allPr={0} allAuto mpFlex>
            <div>
              <span {...bemClasses('text', 'main')} children={beautifyNumber(housesCounter.ready_house_number || 0)} />
            </div>
            <div>
              <span {...bemClasses('text', 'info')} children={`${numDescOnlyText(
                housesCounter.ready_house_number || 0,
                i18n('developerHouseReady'),
                i18n('developerHouseReadyDeclination'),
                i18n('developerHouseReadyMultiple')
              )} ${i18n('developerInCount', { placeholder: beautifyNumber(housesCounter.ready_house_in_lc_number || 0) })}${minBuiltYear}`} />
            </div>
          </Col>
          <Col {...bemClasses('col', 'data')} allPr={0} allAuto mpFlex>
            <div>
              <span {...bemClasses('text', 'main')} children={beautifyNumber(housesCounter.in_progress_house_number || 0)} />
            </div>
            <div>
              <span
                {...bemClasses('text', 'info')}
                children={
                  `${numDescOnlyText(
                    housesCounter.in_progress_house_number || 0,
                    i18n('developerHouseBuilt'),
                    i18n('developerHouseBuiltDeclination'),
                    i18n('developerHouseBuiltMultiple')
                  )} ${i18n(
                    'developerInCount',
                    { placeholder: beautifyNumber(housesCounter.in_progress_house_in_lc_number || 0) }
                  )}`
                }
              />
            </div>
          </Col>
          {+get(dev_index_by_lc, 'count_lc') >=2 &&
            <Col {...bemClasses('col', 'data reliability')} allPr={0} allAuto mpFlex>
              <div>
                <span {...bemClasses('text', 'main')}>
                  <span {...bemClasses('text', 'points')} children={dev_index_by_lc.filled} />
                  <span {...bemClasses('text', 'pointsAll')} children={`${i18n('from')} ${dev_index_by_lc.all}`} />
                </span>
              </div>
              <div>
                <span
                  {...bemClasses('text', 'info')}
                  children={i18n('middleReliabilityIndex')}
                />
                <ToolTip
                  {...bemClasses('tooltip')}
                  titleHtml={
                    <div
                      {...bemClasses('tooltipContent')}
                      children={i18n('middleReliabilityIndexDescription')}
                    />
                  }
                >
                  <Icon {...bemClasses('icon', 'tooltipHelp')} type={iconTypes.helpCircle} />
                </ToolTip>
              </div>
            </Col>
          }
        </Row>
      </div>
    );
  }
}
